import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  ApiError,
  retentionApi,
  storageApi,
  type RetentionDeletionView,
  type RetentionView,
  type StorageDraft,
  type StorageKeyBundle,
  type StorageProviderId,
  type StorageTest,
  type StorageView
} from '../../api/client';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Dialog, Field, LoadError, Loading, Notice, PageHeader, SelectField } from '../../components/ui';
import { dateOnly, shortWhen, sizeOf } from '../../lib/format';
import { useData } from '../../lib/useData';
import { RetentionPreview } from './RetentionPreview';

/**
 * Settings → Storage and retention (Storage A1, 1.13.0, Teampace 2716; help: storage): what Stitch
 * keeps of the company's history, and a second copy in the company's own bucket (S3 or
 * S3-compatible; A3: Google Cloud Storage and Azure Blob Storage), copied byte for byte from Stitch's.
 * Company admins only. The key goes in once and is never shown again (only its last four characters).
 */
export function StoragePage() {
  const { tenant } = useTenant();
  const view = useData(() => storageApi.view(tenant.domain), [tenant.domain], { live: false });
  const [changing, setChanging] = useState(false);
  const v = view.data;

  return (
    <>
      <PageHeader eyebrow="Settings" title="Storage and retention" />
      <div className="content">
        <p className="muted">
          <Link to={`/t/${encodeURIComponent(tenant.domain)}/settings`}>← Settings</Link>
        </p>
        {view.error ? (
          <Card>
            <LoadError error={view.error} retry={view.reload} />
          </Card>
        ) : !v ? (
          <Card>
            <Loading label="Loading storage" />
          </Card>
        ) : (
          <>
            <StitchCopyCard v={v} company={tenant.name} />
            <MessageSearchCard domain={tenant.domain} v={v} company={tenant.name} onSaved={view.reload} />
            <DownloadEverythingCard domain={tenant.domain} v={v} company={tenant.name} onSaved={view.reload} />
            <RetentionCard domain={tenant.domain} company={tenant.name} />
            {v.connection && !changing ? (
              <ConnectedCard domain={tenant.domain} v={v} onChange={() => setChanging(true)} onSaved={view.reload} />
            ) : (
              <ConnectCard
                domain={tenant.domain}
                v={v}
                company={tenant.name}
                onCancel={v.connection ? () => setChanging(false) : undefined}
                onConnected={() => {
                  setChanging(false);
                  view.reload();
                }}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

function StitchCopyCard({ v, company }: { v: StorageView; company: string }) {
  const s = v.stitchCopy;
  return (
    <Card title="What Stitch keeps" action={<Chip tone={s.ready ? 'ok' : 'warn'}>{s.ready ? 'Archiving' : 'Being set up'}</Chip>}>
      <div className="card-body stack">
        <p className="muted small">
          Stitch archives every one of {company}’s calls in open files (Parquet), in its own storage, so History can reach back further than the phone system keeps. Add your own storage
          below and you get the same files in your bucket, yours to keep even if you leave Stitch.
        </p>
        <dl className="facts">
          <dt>Days archived</dt>
          <dd>{s.days.toLocaleString('en-US')}</dd>
          <dt>Size</dt>
          <dd>{sizeOf(s.bytes)}</dd>
          <dt>Since</dt>
          <dd>{s.firstDay ? dateOnly(`${s.firstDay}T12:00:00Z`) : 'Nothing archived yet'}</dd>
        </dl>
      </div>
    </Card>
  );
}

/**
 * Whether the company's admins may search every archived message in History (2716; off by default:
 * each person sees the conversations they're in). Turning it on is logged, and Messages tells
 * everyone while it's on.
 */
function MessageSearchCard({ domain, v, company, onSaved }: { domain: string; v: StorageView; company: string; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const on = v.messageSearch.on;
  const set = async (next: boolean) => {
    setBusy(true);
    setError('');
    try {
      await storageApi.setMessageSearch(domain, next);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Archived messages">
      <div className="card-body stack">
        <label className="check-row">
          <input type="checkbox" checked={on} disabled={busy} onChange={(e) => void set(e.target.checked)} />
          <span className="cell-stack">
            <span>Admins can search every archived message</span>
            <small className="muted">
              {on
                ? `On${v.messageSearch.since ? ` since ${dateOnly(v.messageSearch.since)}` : ''}: ${company}’s admins can find any channel’s, direct message’s or text’s archived messages in History. Everyone sees that this is on in Messages.`
                : 'Off: in History, everyone (admins too) finds the messages of conversations they can read in Messages. Private channels and direct messages stay with the people in them.'}
            </small>
          </span>
        </label>
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Card>
  );
}

/**
 * Download everything (2716): the whole archive as one zip, prepared by Stitch's archive (a few
 * minutes, longer for a large archive) and emailed to whoever asked when it's ready; then
 * downloadable here for 7 days. Each download is logged.
 */
function DownloadEverythingCard({ domain, v, company, onSaved }: { domain: string; v: StorageView; company: string; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const waiting = v.exports.find((e) => e.status === 'waiting');
  const ask = async () => {
    setBusy(true);
    setError('');
    try {
      await storageApi.requestExport(domain);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const shown = v.exports.filter((e) => e.status !== 'expired').slice(0, 3);
  return (
    <Card
      title="Download everything"
      action={
        waiting ? (
          <Button variant="ghost" icon="refresh" onClick={onSaved}>
            Refresh
          </Button>
        ) : undefined
      }
    >
      <div className="card-body stack">
        <p className="muted small">
          All of {company}’s archive as one zip: the same folders as in the archive (calls, voicemail, messages, Patches, transcripts, recordings and files), with a list of every file
          and its checksum. Stitch prepares it, then emails you when it’s ready to download here, for 7 days.
        </p>
        {shown.map((e) => (
          <div key={e.id} className="export-row">
            {e.status === 'waiting' && (
              <>
                <Chip tone="warn">Being prepared</Chip>
                <span className="muted small">
                  Asked for by {e.requestedByName || 'an admin'}, {shortWhen(e.createdAt)}. You’ll get an email when it’s ready.
                </span>
              </>
            )}
            {e.status === 'ready' && (
              <>
                <Chip tone="ok">Ready</Chip>
                <span className="small">
                  {e.files.toLocaleString('en-US')} files, {sizeOf(e.bytes)}
                  {e.expiresAt ? `, until ${dateOnly(e.expiresAt)}` : ''}
                  {e.downloads ? ` · downloaded ${e.downloads === 1 ? 'once' : `${e.downloads} times`}` : ''}
                </span>
                <a className="btn primary small" href={storageApi.exportUrl(domain, e.id)} download>
                  Download
                </a>
              </>
            )}
            {e.status === 'failed' && (
              <>
                <Chip tone="crit">Couldn’t be made</Chip>
                <span className="muted small">{e.error ?? 'Stitch tried three times.'}</span>
              </>
            )}
          </div>
        ))}
        {error && <Notice tone="crit">{error}</Notice>}
        <div>
          <Button variant={shown.some((e) => e.status === 'ready') ? 'secondary' : 'primary'} disabled={busy || Boolean(waiting)} onClick={() => void ask()}>
            {waiting ? 'Being prepared…' : busy ? 'Asking…' : shown.some((e) => e.status === 'ready') ? 'Prepare a new one' : 'Prepare a download'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

const dayText = (d: string) => dateOnly(`${d}T12:00:00Z`);

/**
 * How long Stitch keeps each kind of record (Storage A2, 2717; help: storage): 24 months by default,
 * included; shorter or longer is the company's choice (longer: billed for what's older than 24 months).
 * Each night Stitch counts what's past each choice in its own copy; the first count with anything in it
 * is confirmed here before anything is deleted. Once Stitch has turned deleting on (deletionsOn), what
 * was confirmed goes from Stitch's copy that night, and what passes the days after it each night after
 * (part 2); the last deletion says what went, or why nothing did (a hold, or the days changed).
 */
function RetentionCard({ domain, company }: { domain: string; company: string }) {
  const data = useData(() => retentionApi.view(domain), [domain], { live: false });
  const [busy, setBusy] = useState('');
  const [asking, setAsking] = useState('');
  const [error, setError] = useState('');
  const r = data.data;
  const act = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    setError('');
    try {
      await fn();
      setAsking('');
      data.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  };
  if (data.error)
    return (
      <Card title="How long Stitch keeps things">
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  if (!r)
    return (
      <Card title="How long Stitch keeps things">
        <Loading label="Loading retention" />
      </Card>
    );
  const label = (days: number) => r.choices.find((c) => c.days === days)?.label ?? `${days} days`;
  const price = `$${(r.extraCentsPer100Gb / 100).toFixed(2)} per 100 GB a month`;
  return (
    <Card
      title="How long Stitch keeps things"
      action={
        <Button variant="ghost" icon="refresh" disabled={Boolean(busy)} onClick={() => void act('count', () => retentionApi.count(domain))}>
          {busy === 'count' ? 'Counting…' : 'Count now'}
        </Button>
      }
    >
      <div className="card-body stack">
        <p className="muted small">
          Stitch keeps each kind of {company}’s records for {label(r.includedDays)} by default, included in every plan. Choose shorter or longer for each kind. Longer than {label(r.includedDays)} is {price} for
          what’s older than {label(r.includedDays)}, on your invoice. This is Stitch’s copy only: what’s in your own storage is yours, and stays.
        </p>
        {r.holds.company && <Notice tone="warn">A legal hold covers all of {company}’s records: nothing is deleted while it’s on.</Notice>}
        {!r.deletionsOn && (
          <Notice tone="neutral">
            Nothing is deleted yet. Each night Stitch counts what’s past each choice, and you confirm the first count before anything goes. Deleting starts once Stitch turns it on.
          </Notice>
        )}
        <div className="retention-rows">
          {r.kinds.map((k) => {
            const run = k.latest;
            return (
              <div key={k.kind} className="retention-row">
                <SelectField
                  label={k.label}
                  value={k.chosen === null ? '' : String(k.chosen)}
                  disabled={Boolean(busy)}
                  onChange={(e) => void act(`set:${k.kind}`, () => retentionApi.set(domain, k.kind, e.target.value ? Number(e.target.value) : null))}
                  options={[
                    { value: '', label: `${label(r.defaultDays)} (Stitch’s default)` },
                    ...r.choices.map((c) => ({ value: String(c.days), label: c.extra ? `${c.label} (billed past ${label(r.includedDays)})` : c.label }))
                  ]}
                />
                <div className="small retention-status">
                  {!run ? (
                    <span className="muted">Not counted yet for {label(k.days)}.</span>
                  ) : run.outcome === 'held' ? (
                    <span>
                      <Chip tone="warn">On hold</Chip> Nothing is deleted while the hold is on.
                    </span>
                  ) : run.outcome === 'nothing' ? (
                    <span className="muted">Nothing is from before {dayText(run.cutoff)}.</span>
                  ) : (
                    <span className="cell-stack">
                      <span>
                        {run.items.toLocaleString('en-US')} {k.noun} ({sizeOf(run.bytes)}) {run.items === 1 ? 'is' : 'are'} from before {dayText(run.cutoff)}
                        {run.holds ? `; what ${run.holds === 1 ? 'a hold covers' : `${run.holds} holds cover`} stays` : ''}.
                      </span>
                      {run.outcome === 'confirmed' ? (
                        <small className="muted">
                          Confirmed{k.confirmed ? ` by ${k.confirmed.byName || 'an admin'}, ${dateOnly(k.confirmed.at)}` : ''}.{' '}
                          {!r.deletionsOn
                            ? 'Stitch deletes them once deleting is turned on.'
                            : k.pending
                              ? k.pending.note || 'Stitch deletes them within the hour.'
                              : ''}
                        </small>
                      ) : asking === run.id ? (
                        <>
                          {/* What would go, by month and day, before anything is confirmed (3123, proposal 26). */}
                          <RetentionPreview domain={domain} runId={run.id} />
                          <span className="row-actions">
                            <Button variant="danger" disabled={Boolean(busy)} onClick={() => void act(`confirm:${k.kind}`, () => retentionApi.confirm(domain, run.id))}>
                              {busy === `confirm:${k.kind}` ? 'Confirming…' : `Yes, delete what’s older than ${label(k.days)}`}
                            </Button>
                            <Button variant="ghost" onClick={() => setAsking('')}>
                              Not now
                            </Button>
                          </span>
                        </>
                      ) : (
                        <span>
                          <Button variant="secondary" disabled={Boolean(busy)} onClick={() => setAsking(run.id)}>
                            Confirm
                          </Button>
                        </span>
                      )}
                    </span>
                  )}
                  {k.lastDeletion && <LastDeletion d={k.lastDeletion} noun={k.noun} />}
                </div>
              </div>
            );
          })}
        </div>
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Card>
  );
}

/** The last deletion carried out for a kind: what went from Stitch's copy, or why nothing did. */
function LastDeletion({ d, noun }: { d: RetentionDeletionView; noun: string }) {
  if (d.state === 'done')
    return (
      <small className="muted">
        Deleted {d.items.toLocaleString('en-US')} {noun} ({sizeOf(d.bytes)}) from before {dayText(d.cutoff)}, {dateOnly(d.at)}.
      </small>
    );
  return (
    <small className={d.state === 'failed' ? 'crit-text' : 'muted'}>
      {dateOnly(d.at)}: {d.note || (d.state === 'failed' ? 'Deleting didn’t finish. Stitch has been told.' : 'Nothing was deleted.')}
    </small>
  );
}

/** Where the company's copy stands, and what can be done with it. */
function ConnectedCard({ domain, v, onChange, onSaved }: { domain: string; v: StorageView; onChange: () => void; onSaved: () => void }) {
  const c = v.connection!;
  const st = v.status;
  const [busy, setBusy] = useState('');
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const [steps, setSteps] = useState<StorageTest['steps'] | null>(null);
  const [removing, setRemoving] = useState(false);

  async function run(what: string, fn: () => Promise<string>) {
    setBusy(what);
    setSaid(null);
    try {
      setSaid({ tone: 'ok', text: await fn() });
      onSaved();
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof ApiError || err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy('');
    }
  }

  const copying = st?.backfill && st.backfill.done < st.backfill.total;
  const failing = Boolean(c.lastError);
  const azure = c.provider === 'azure';
  // An Azure SAS token ends (A3): said a month ahead.
  const tokenEndsSoon = Boolean(c.keyExpiresAt && Date.parse(c.keyExpiresAt) - Date.now() < 30 * 86_400_000);
  const chip = failing ? { tone: 'crit' as const, label: 'Not answering' } : copying ? { tone: 'brand' as const, label: 'Copying your history' } : st && st.waiting > 0 ? { tone: 'brand' as const, label: 'Copying' } : { tone: 'ok' as const, label: 'Up to date' };

  return (
    <Card title="Your storage" action={<Chip tone={chip.tone}>{chip.label}</Chip>}>
      <div className="card-body stack">
        {failing && (
          <Notice tone="crit" title={`Stitch can’t write to ${c.bucket} (since ${shortWhen(st?.behindSince ?? c.lastError!.at)})`}>
            {c.lastError!.said}. Nothing is lost: Stitch keeps its own copy, and yours catches up by itself when the bucket answers. Check the key and the bucket, then press Test.
          </Notice>
        )}
        {tokenEndsSoon && !failing && (
          <Notice tone="warn" title={`The SAS token ends ${dateOnly(c.keyExpiresAt!)}`}>
            Make a new token for the container in the Azure portal before then, and connect it with Change (same container and folder: Stitch picks up where it left off).
          </Notice>
        )}
        {copying && st?.backfill && (
          <div className="stack">
            <progress className="storage-progress" max={st.backfill.total} value={st.backfill.done} aria-label="Copying your history" />
            <p className="muted small">
              Copying what Stitch already keeps: {st.backfill.done.toLocaleString('en-US')} of {st.backfill.total.toLocaleString('en-US')} files, oldest day first.
            </p>
          </div>
        )}
        <dl className="facts">
          <dt>Storage</dt>
          <dd>{c.providerName}</dd>
          <dt>{azure ? 'Container' : 'Bucket'}</dt>
          <dd>
            {c.bucket} / {c.folder}
          </dd>
          <dt>{azure ? 'SAS token' : 'Key'}</dt>
          <dd>
            Ends …{c.keyIdEnd}
            {c.keyExpiresAt ? `, works until ${dateOnly(c.keyExpiresAt)}` : ''}
          </dd>
          <dt>Files there</dt>
          <dd>{c.mode === 'sealed' && c.seal ? `Sealed with archive key ${c.seal.keyId} (fingerprint ${c.seal.fingerprint})` : 'Readable, as in Stitch’s copy'}</dd>
          <dt>Deleting there</dt>
          <dd>{c.deletesBy === 'company' ? 'Your bucket’s own rules (Stitch never deletes)' : 'Stitch keeps your retention there too'}</dd>
          <dt>Last written</dt>
          <dd>{c.lastWriteAt ? shortWhen(c.lastWriteAt) : 'Nothing yet'}</dd>
          <dt>Waiting</dt>
          <dd>{(st?.waiting ?? 0).toLocaleString('en-US')} files</dd>
          <dt>In your storage</dt>
          <dd>
            {sizeOf(st?.bytes ?? 0)}, {(st?.days ?? 0).toLocaleString('en-US')} days
          </dd>
          <dt>Connected</dt>
          <dd>
            {shortWhen(c.createdAt)}
            {c.createdByName ? ` by ${c.createdByName}` : ''}
            {c.adopted ? ' (picked up the archive already there)' : ''}
          </dd>
        </dl>
        <div className="row-actions">
          <Button
            variant="secondary"
            disabled={Boolean(busy)}
            onClick={() =>
              void run('test', async () => {
                const r = await storageApi.testSaved(domain);
                setSteps(r.test.steps);
                if (!r.test.ok) throw new Error('The test didn’t pass. See the steps below.');
                return 'Every step passed.';
              })
            }
          >
            Test
          </Button>
          <Button
            variant="ghost"
            disabled={Boolean(busy) || Boolean(copying)}
            title="Copy everything Stitch keeps again (files already there are written again)"
            onClick={() => void run('backfill', async () => `${(await storageApi.backfill(domain)).queued.toLocaleString('en-US')} files queued, oldest day first.`)}
          >
            Copy everything again
          </Button>
          <Button
            variant="ghost"
            disabled={Boolean(busy)}
            onClick={() =>
              void run('deletes', async () => {
                const next = c.deletesBy === 'company' ? 'stitch' : 'company';
                await storageApi.setDeletesBy(domain, next);
                return next === 'stitch' ? 'Stitch now keeps your retention in your storage too.' : 'Stitch won’t delete anything in your storage; your bucket’s rules decide.';
              })
            }
          >
            {c.deletesBy === 'company' ? 'Let Stitch keep retention there' : 'Let my bucket’s rules decide'}
          </Button>
          <Button variant="ghost" disabled={Boolean(busy)} onClick={onChange}>
            Change
          </Button>
          <Button variant="danger" disabled={Boolean(busy)} onClick={() => setRemoving(true)}>
            Remove
          </Button>
        </div>
        {steps && <TestSteps steps={steps} />}
        {said && <Notice tone={said.tone}>{said.text}</Notice>}
        {c.test && (
          <p className="muted small-print">
            Last tested {shortWhen(c.test.at)}: {c.test.said}
          </p>
        )}
      </div>
      <Dialog
        open={removing}
        onClose={() => setRemoving(false)}
        title="Remove your storage?"
        foot={
          <>
            <Button variant="ghost" onClick={() => setRemoving(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={Boolean(busy)}
              onClick={() =>
                void run('remove', async () => {
                  await storageApi.remove(domain);
                  setRemoving(false);
                  return 'Removed. Stitch has forgotten the key; the files stay in your bucket.';
                })
              }
            >
              Remove
            </Button>
          </>
        }
      >
        <p>
          Stitch stops copying to {c.bucket} and forgets the key. The files already there stay: they’re yours. Once Stitch’s own copy of a day is gone, History can’t reach it any more unless
          you connect this storage again (Stitch picks up the archive already there).
        </p>
        {c.mode === 'sealed' && (
          <Notice tone="warn" title="Keep your key bundle">
            The files there are sealed. Stitch forgets the archive key too, so after this only your key bundle ({c.seal?.keyId}) opens them, with the stitch-unseal.mjs tool in the folder.
          </Notice>
        )}
      </Dialog>
    </Card>
  );
}

/**
 * The account and container from a pasted Blob SAS URL (https://<account>.blob.core.windows.net/<container>?sv=…).
 * Another address (Azurite, a national cloud) sets the address itself. Null: not a URL.
 */
export function fromBlobSasUrl(value: string): Partial<StorageDraft> | null {
  if (!/^https?:\/\//i.test(value.trim())) return null;
  try {
    const u = new URL(value.trim());
    const parts = u.pathname.split('/').filter(Boolean);
    const standard = /^([a-z0-9]{3,24})\.blob\.core\.windows\.net$/.exec(u.hostname);
    if (standard) return parts[0] ? { region: standard[1]!, bucket: parts[0] } : { region: standard[1]! };
    return parts.length >= 2 ? { endpoint: `${u.origin}/${parts[0]}`, bucket: parts[1]! } : null;
  } catch {
    return null;
  }
}

function TestSteps({ steps }: { steps: StorageTest['steps'] }) {
  return (
    <ul className="reg-list">
      {steps.map((x) => (
        <li key={x.step} className={x.ok && x.warn ? 'warn-text' : undefined}>
          <strong>{!x.ok ? 'Failed' : x.warn ? 'Check' : 'OK'}:</strong> {x.step}. {x.said}
        </li>
      ))}
    </ul>
  );
}

/** Connecting (or changing) the company's storage: where, the key and its policy, the test, then connect. */
function ConnectCard({ domain, v, company, onCancel, onConnected }: { domain: string; v: StorageView; company: string; onCancel: (() => void) | undefined; onConnected: () => void }) {
  const old = v.connection;
  const [d, setD] = useState<StorageDraft>({
    provider: old?.provider ?? 'aws',
    endpoint: old?.endpoint ?? '',
    region: old?.region ?? '',
    bucket: old?.bucket ?? '',
    folder: old?.folder ?? 'stitch-archive',
    keyId: '',
    secret: '',
    deletesBy: old?.deletesBy ?? 'company'
  });
  const [backfill, setBackfill] = useState(true);
  // Sealed mode (A3): the key bundle Stitch just made (downloaded), and the one chosen back from disk.
  const [made, setMade] = useState<{ keyId: string; fingerprint: string } | null>(null);
  const [bundleSaid, setBundleSaid] = useState<{ tone: 'ok' | 'crit' | 'warn'; text: string } | null>(null);
  const [policy, setPolicy] = useState<{ steps: string[]; policy: string | null } | null>(null);
  const [steps, setSteps] = useState<StorageTest['steps'] | null>(null);
  const [busy, setBusy] = useState('');
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit' | 'warn'; text: string } | null>(null);
  const p = v.providers.find((x) => x.id === d.provider) ?? v.providers[0]!;
  const set = <K extends keyof StorageDraft>(k: K, value: StorageDraft[K]) => setD((x) => ({ ...x, [k]: value }));

  // The address follows the region (Azure: the storage account) for services that have one per region.
  const perRegion = Boolean(p.endpointTemplate?.includes('{region}'));
  const suggested = useMemo(() => (p.endpointTemplate && (d.region || p.regionHint) ? p.endpointTemplate.replace('{region}', d.region || p.regionHint) : ''), [p, d.region]);
  useEffect(() => {
    if (p.endpointTemplate) setD((x) => ({ ...x, endpoint: suggested }));
  }, [suggested, p.endpointTemplate]);
  useEffect(() => {
    const t = setTimeout(() => {
      storageApi
        .policy(domain, { provider: d.provider, bucket: d.bucket, folder: d.folder, deletesBy: d.deletesBy })
        .then(setPolicy)
        .catch(() => setPolicy(null));
    }, 300);
    return () => clearTimeout(t);
  }, [domain, d.provider, d.bucket, d.folder, d.deletesBy]);

  const ready = Boolean(d.endpoint && d.bucket && (!p.keyIdLabel || d.keyId.trim().length >= 4) && d.secret.trim().length >= 8 && (d.mode !== 'sealed' || d.keyBundle));
  const makeBundle = async () => {
    setBusy('bundle');
    setBundleSaid(null);
    try {
      const r = await storageApi.sealKey(domain);
      const url = URL.createObjectURL(new Blob([JSON.stringify(r.bundle, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = r.fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setMade({ keyId: r.bundle.keyId, fingerprint: r.bundle.fingerprint ?? '' });
      setD((x) => ({ ...x, keyBundle: undefined }));
      setBundleSaid({ tone: 'warn', text: `Downloaded ${r.fileName}. Keep it somewhere safe (not only on this computer): Stitch can’t give it to you again. Then choose it below.` });
    } catch (err) {
      setBundleSaid({ tone: 'crit', text: err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy('');
    }
  };
  const chooseBundle = async (file: File | undefined) => {
    if (!file) return;
    try {
      const b = JSON.parse(await file.text()) as StorageKeyBundle;
      if (b?.format !== 'stitch-archive-key' || typeof b.keyId !== 'string' || typeof b.key !== 'string') throw new Error('That isn’t a Stitch key bundle.');
      setD((x) => ({ ...x, keyBundle: b }));
      setBundleSaid(
        made && made.keyId !== b.keyId
          ? { tone: 'warn', text: `That’s key bundle ${b.keyId}, not the one just made (${made.keyId}). That’s fine for a folder already sealed with it.` }
          : { tone: 'ok', text: `Key bundle ${b.keyId}${b.fingerprint ? ` (fingerprint ${b.fingerprint})` : ''} is ready.` }
      );
    } catch (err) {
      setD((x) => ({ ...x, keyBundle: undefined }));
      setBundleSaid({ tone: 'crit', text: err instanceof Error && err.message.startsWith('That') ? err.message : 'That isn’t a Stitch key bundle.' });
    }
  };
  // Azure (A3): a pasted Blob SAS URL fills in the account and the container.
  const setSecret = (value: string) => {
    const from = d.provider === 'azure' ? fromBlobSasUrl(value) : null;
    setD((x) => ({ ...x, secret: value, ...(from ?? {}) }));
  };
  const draft = (): StorageDraft => ({ ...d, region: p.fixedRegion ?? d.region });

  async function run(what: string, fn: () => Promise<void>) {
    setBusy(what);
    setSaid(null);
    try {
      await fn();
    } catch (err) {
      const test = err instanceof ApiError ? ((err.body.test as StorageTest | undefined) ?? null) : null;
      if (test) setSteps(test.steps);
      setSaid({ tone: 'crit', text: err instanceof ApiError || err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy('');
    }
  }

  return (
    <Card title={old ? 'Change your storage' : 'Add your own storage'}>
      <div className="card-body stack">
        <p className="muted small">
          Keep a second copy of {company}’s history in your own bucket: every call, and the same open files Stitch keeps. Stitch copies each file there after its own copy is checked, so the
          two never differ. Use a private bucket.
        </p>
        {!v.canSeal && <Notice tone="warn">Stitch can’t keep a storage key safely here yet. Ask Stitch support.</Notice>}
        <SelectField
          label="Storage"
          value={d.provider}
          // A region (or Azure's storage account) from another provider wouldn't fit.
          onChange={(e) => setD((x) => ({ ...x, provider: e.target.value as StorageProviderId, region: '' }))}
          options={v.providers.map((x) => ({ value: x.id, label: x.name }))}
        />
        {perRegion && <Field label={p.regionLabel} placeholder={p.regionHint} value={d.region} onChange={(e) => set('region', e.target.value.trim())} spellCheck={false} />}
        <Field
          label={p.endpointLabel}
          placeholder={p.endpointTemplate ? suggested : d.provider === 'r2' ? 'https://<account id>.r2.cloudflarestorage.com' : 'https://storage.example.com'}
          value={d.endpoint}
          onChange={(e) => set('endpoint', e.target.value.trim())}
          spellCheck={false}
          hint={perRegion ? `Filled in from the ${p.regionLabel.toLowerCase()}.` : p.endpointTemplate ? 'Filled in for you.' : undefined}
        />
        {!p.endpointTemplate && !p.fixedRegion && <Field label="Region (if your storage asks for one)" placeholder={p.regionHint} value={d.region} onChange={(e) => set('region', e.target.value.trim())} spellCheck={false} />}
        <Field label={p.bucketLabel} value={d.bucket} onChange={(e) => set('bucket', e.target.value.trim())} spellCheck={false} />
        <Field label="Folder in the bucket" value={d.folder} onChange={(e) => set('folder', e.target.value.trim())} spellCheck={false} hint="Stitch writes only inside this folder." />
        <fieldset className="plain-fieldset stack">
          <legend>Deleting in your storage</legend>
          <label className="check">
            <input type="radio" name="deletes" checked={d.deletesBy === 'company'} onChange={() => set('deletesBy', 'company')} /> Your bucket’s own rules decide (recommended): Stitch never
            deletes there, and the key doesn’t need to delete.
          </label>
          <label className="check">
            <input type="radio" name="deletes" checked={d.deletesBy === 'stitch'} onChange={() => set('deletesBy', 'stitch')} /> Stitch keeps your retention there too (the key must be able to delete).
          </label>
        </fieldset>
        <fieldset className="plain-fieldset stack">
          <legend>Your files there</legend>
          <label className="check">
            <input type="radio" name="mode" checked={d.mode !== 'sealed'} onChange={() => setD((x) => ({ ...x, mode: 'readable', keyBundle: undefined }))} /> Readable (recommended): the same open files as
            Stitch’s copy; who can read them is your bucket’s own access control.
          </label>
          <label className="check">
            <input type="radio" name="mode" checked={d.mode === 'sealed'} onChange={() => setD((x) => ({ ...x, mode: 'sealed' }))} /> Sealed: Stitch encrypts every file before it reaches your storage,
            with an archive key you download and keep. Someone who gets into the bucket can’t read anything without it.
          </label>
          {d.mode === 'sealed' && (
            <div className="stack">
              <p className="muted small">
                Stitch keeps a sealed copy of the key while this storage is connected, so History can still read it. If you remove the storage or leave Stitch, only your key bundle opens the
                files (with the stitch-unseal.mjs tool Stitch puts in the folder). Lose it, and nobody can open them.
              </p>
              <div className="row-actions">
                <Button variant="secondary" disabled={Boolean(busy)} onClick={() => void makeBundle()}>
                  {busy === 'bundle' ? 'Making…' : 'Make and download a key bundle'}
                </Button>
              </div>
              <Field
                label="Choose the key bundle you saved"
                type="file"
                accept="application/json,.json"
                onChange={(e) => void chooseBundle(e.target.files?.[0])}
                hint="Or one you already have, for a folder sealed with it."
              />
              {bundleSaid && <Notice tone={bundleSaid.tone}>{bundleSaid.text}</Notice>}
            </div>
          )}
        </fieldset>
        {policy && (
          <div className="stack">
            <h3 className="storage-heading">Make a key for Stitch</h3>
            <ol className="reg-list">
              {policy.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
            {policy.policy && (
              <>
                <pre className="storage-policy" aria-label="Policy">
                  {policy.policy}
                </pre>
                <div>
                  <Button size="small" variant="ghost" onClick={() => void navigator.clipboard?.writeText(policy.policy!).then(() => setSaid({ tone: 'ok', text: 'Policy copied.' }))}>
                    Copy the policy
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
        {p.keyIdLabel && <Field label={p.keyIdLabel} value={d.keyId} onChange={(e) => set('keyId', e.target.value)} autoComplete="off" spellCheck={false} />}
        <Field
          label={p.secretLabel}
          type="password"
          value={d.secret}
          onChange={(e) => setSecret(e.target.value)}
          autoComplete="new-password"
          hint={d.provider === 'azure' ? 'Kept sealed; never shown again. A Blob SAS URL fills in the account and container.' : 'Kept sealed; never shown again.'}
        />
        <label className="check">
          <input type="checkbox" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} /> Copy everything Stitch already keeps (recommended)
        </label>
        <div className="row-actions">
          <Button
            variant="secondary"
            disabled={!ready || Boolean(busy)}
            onClick={() =>
              void run('test', async () => {
                const r = await storageApi.test(domain, draft());
                setSteps(r.steps);
                setSaid(r.ok ? { tone: 'ok', text: r.adopted ? 'Every step passed, and this folder already holds your archive: Stitch picks up where it left off.' : 'Every step passed. Press Connect.' } : { tone: 'crit', text: 'The test didn’t pass. See the steps below.' });
              })
            }
          >
            Test
          </Button>
          <Button
            variant="primary"
            disabled={!ready || Boolean(busy) || !v.canSeal}
            onClick={() =>
              void run('connect', async () => {
                const r = await storageApi.connect(domain, { ...draft(), backfill });
                setSteps(r.test.steps);
                onConnected();
              })
            }
          >
            Connect
          </Button>
          {onCancel && (
            <Button variant="ghost" disabled={Boolean(busy)} onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
        {old && <p className="muted small-print">Connecting another bucket replaces {old.bucket}: Stitch forgets its key, and the files there stay.</p>}
        {steps && <TestSteps steps={steps} />}
        {said && <Notice tone={said.tone}>{said.text}</Notice>}
      </div>
    </Card>
  );
}
